import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { filter } from 'rxjs/operators';
import { ngForFadeInOpacity, ngForFadeInOut } from 'src/app/animations';
import { ISlide } from 'src/app/core/dtos/interfaces/i-slide';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ISlideGroup } from '../../interfaces/i-slide-group';
import { SlideEvent } from 'src/app/core/dtos/interfaces/i-slide-event';
import { MediaService } from 'src/app/shared/services/media.service';
import { Observable } from 'rxjs';
import { ContentEditorComponent } from '../content-editor/content-editor.component';

@Component({
  selector: 'app-slide',
  templateUrl: './slide.component.html',
  styleUrls: ['./slide.component.scss'],
  animations: [ngForFadeInOpacity, ngForFadeInOut],
})
export class SlideComponent implements OnInit, OnChanges {
  @Input() slide: ISlide;
  @Input() index: number;
  @Input() changingSlideOrder$: Observable<boolean>;
  @Input() isDisabled = false;

  @Output() slideOrderChanged = new EventEmitter<number[]>();
  @Output() slideChanged = new EventEmitter<SlideEvent>();
  @Output() removeSlideClick = new EventEmitter<number>();
  @Output() bookmarkChanged = new EventEmitter<ISlide>();

  title = 'Slide';
  iconClass: string;
  isExpandedByDefault = true;
  isExpandable = true;
  imgName: string;
  isTyping = false;

  @ViewChild(ContentEditorComponent)
  contentEditorComponent: ContentEditorComponent;

  form = new FormGroup<ISlideGroup>({
    id: new FormControl(),
    order: new FormControl(),
    body: new FormControl(),
    mainImage: this.formBuilder.array([]),
    inlineImages: this.formBuilder.array([]),
    inlineVideos: this.formBuilder.array([]),
  });

  @ViewChild(MatExpansionPanel) matExpansionPanel: MatExpansionPanel;

  isExpanded = false;
  editorFocused: boolean = false;

  HEADER_HEIGHT = '43px';
  testcontrol = new FormControl('test');

  constructor(
    public _dialogService: DialogService,
    public mediaService: MediaService,
    public formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  onOpen(_): void {
    this.isExpanded = true;
  }

  onClose(_): void {
    this.isExpanded = false;
  }

  openPanel() {
    if (this.isExpandable) {
      this.matExpansionPanel.open();
    }
  }

  private initForm(): void {
    if (this.isDisabled) {
      this.form.disable();
    }

    this.form.controls.id.setValue(this.slide.id);
    this.form.controls.body.setValue(this.slide.body);
    this.slide.inlineImages?.forEach((img) => {
      this.form.controls.inlineImages.push(new FormControl(img));
    });

    this.slide.inlineVideos?.forEach((video) => {
      this.form.controls.inlineVideos.push(new FormControl(video));
    });

    this.form.controls.order.setValue(this.slide.order);
    if (this.slide.mediaId) {
      this.form.controls.mainImage.push(
        new FormControl(this.mediaService.mapSlideToImage(this.slide))
      );
    }
    if (this.slide.mediaTitle) {
      this.imgName = this.slide.mediaTitle;
    }

    this.initFormChanges();
  }

  private initFormChanges(): void {
    this.form.controls.order.valueChanges.subscribe({
      next: (value) => {
        if (value > 0 && !this.isTyping) {
          this.slideOrderChanged.emit([value, this.slide.order]);
        }
      },
    });

    this.form.controls.mainImage.valueChanges.subscribe({
      next: (value) => {
        if (value.length === 0 || value[0].name === '') {
          this.imgName = null;
          return;
        }
        var newTitle = value[0].name;
        if (newTitle) {
          this.imgName = newTitle;
        }
      },
    });

    if (!this.isDisabled) {
      this.form.valueChanges.subscribe({
        next: (value) => {
          if (this.isTyping) {
            return;
          }
          var changedSlide = this.mediaService.mapImageToSlide(
            value.mainImage[0],
            value.id,
            value.order
          );
          changedSlide.body = value.body;
          changedSlide.inlineImages = value.inlineImages;
          changedSlide.inlineVideos = value.inlineVideos;
          this.slideChanged.emit(new SlideEvent(this.index, changedSlide));
        },
      });
    }
  }

  emitBookmark(): void {
    this.bookmarkChanged.emit(this.slide);
  }

  setEditorListeners(): void {
    const editor = this.contentEditorComponent.editor;

    editor.on('focus', () => {
      this.editorFocused = true;
    });

    editor.on('blur', () => {
      this.editorFocused = false;
    });

    editor.on('nodeChange', () => {
      if (this.editorFocused) {
        this.emitBookmark();
      }
    });
  }

  ngOnChanges(): void {
    this.form.controls.order.setValue(this.slide.order, { emitEvent: false });
  }

  handleInput(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];

    const isNumber = event.key >= '0' && event.key <= '9';

    if (event.key === 'Enter') {
      this.applySlideOrder(event);
    }

    if (allowedKeys.includes(event.key) || isNumber) {
      this.isTyping = true;
      return;
    } else {
      event.preventDefault();
    }

    event.stopPropagation();
  }

  applySlideOrder(event: KeyboardEvent) {
    event.stopPropagation();
    this.isTyping = false;
    this.slideOrderChanged.emit([
      this.form.controls.order.value,
      this.slide.order,
    ]);
  }

  removeSlide(event: KeyboardEvent): void {
    event.stopPropagation();
    this._dialogService
      .openConfirmationDialog(
        {
          title: 'Delete Slide?',
          message: 'Are you sure you want to delete this slide?',
          confirmLabel: 'Yes',
        },
        {
          top: 'calc(80px + 5%)',
        }
      )
      .afterClosed()
      .pipe(filter((dialogResponse) => !!dialogResponse))
      .subscribe({
        next: () => {
          this.removeSlideClick.emit(this.slide.order - 1);
        },
      });
  }

  onAddToBody($event) {
    this.contentEditorComponent.insertContent($event);
  }

  onRemoveLink($event) {
    this.contentEditorComponent.replaceContent($event.regex, $event.newValue);
  }
}
